import { useState, useEffect, useRef } from 'react';
import { stripHtml } from '../utils/textUtils';

const AutoPlayButton = ({ selectedEvent, isAutoPlaying, onAutoPlayToggle, onNext, onUpdateReadingState }) => {
  const [isReading, setIsReading] = useState(false);
  const speechRef = useRef(null);
  const timeoutRef = useRef(null);

  // isReading değiştiğinde dışarıya bildir
  useEffect(() => {
    if (onUpdateReadingState) {
      onUpdateReadingState(isReading);
    }
  }, [isReading, onUpdateReadingState]);

  // Yeni event seçildiğinde otomatik okuma için useEffect
  useEffect(() => {
    if (isAutoPlaying && selectedEvent) {
      readEventContent(selectedEvent);
    }
  }, [selectedEvent, isAutoPlaying]); // selectedEvent veya isAutoPlaying değiştiğinde tetiklenecek

  useEffect(() => {
    return () => {
      // Cleanup: konuşmayı ve timeout'u temizle
      if (speechRef.current) {
        window.speechSynthesis.cancel();
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const readEventContent = (event) => {
    if (!event) return;

    // Önceki okumayı ve timeout'u temizle
    if (speechRef.current) {
      window.speechSynthesis.cancel();
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // URL'leri metinden temizle
    const cleanText = (text) => {
      // First strip HTML tags to avoid reading font info etc.
      const plainText = stripHtml(text);
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      return plainText.replace(urlRegex, 'link');
    };

    const cleanTitle = cleanText(event.title || '');
    const cleanDescription = cleanText(event.description || '');
    const textToRead = `${cleanTitle}. ${cleanDescription}`;

    const utterance = new SpeechSynthesisUtterance(textToRead);
    utterance.lang = 'tr-TR';

    // Ses ayarlarını al ve uygula
    const volumeControl = document.querySelector('input[type="range"][min="0"][max="1"]');
    const rateControl = document.querySelector('input[type="range"][min="0.5"][max="2"]');
    const pitchControl = document.querySelector('input[type="range"][min="0.5"][max="2"]');

    if (volumeControl) utterance.volume = parseFloat(volumeControl.value);
    if (rateControl) utterance.rate = parseFloat(rateControl.value);
    if (pitchControl) utterance.pitch = parseFloat(pitchControl.value);

    // Konuşma bittiğinde
    utterance.onend = () => {
      setIsReading(false);

      // Konuşma bittikten 1 saniye sonra bir sonraki event'e geç
      if (isAutoPlaying) {
        timeoutRef.current = setTimeout(() => {
          onNext(event);
        }, 1000); // 1 saniye bekle
      }
    };

    // Konuşma sırasında hata olursa
    utterance.onerror = (e) => {
      console.error("Speech synthesis error:", e);
      setIsReading(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };

    speechRef.current = utterance;
    try {
      window.speechSynthesis.speak(utterance);
      setIsReading(true);
    } catch (err) {
      console.error("Speech synthesis failed:", err);
      setIsReading(false);
    }
  };

  const handleAutoPlayToggle = () => {
    if (!selectedEvent) return;

    if (!isAutoPlaying) {
      onAutoPlayToggle();
      // Small timeout to ensure state updates before speaking (optional, but sometimes helps)
      // But for mobile, direct call is better.
      readEventContent(selectedEvent);
    } else {
      // Otomatik oynatmayı, konuşmayı ve timeout'u durdur
      onAutoPlayToggle();
      if (speechRef.current) {
        window.speechSynthesis.cancel();
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setIsReading(false);
    }
  };

  // Okuma sürecini temizle
  const cancelReading = () => {
    if (speechRef.current) {
      window.speechSynthesis.cancel();
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsReading(false);
  };

  // isReading durumunu dışarıdan sorgulamak için
  const getIsReading = () => isReading;

  return (
    <button
      onClick={handleAutoPlayToggle}
      disabled={!selectedEvent}
      className={`${selectedEvent
        ? isAutoPlaying
          ? 'bg-red-500 hover:bg-red-600'
          : 'bg-yellow-500 hover:bg-yellow-600'
        : 'bg-gray-300 cursor-not-allowed'
        } text-white p-1 rounded-md shadow-sm transition-all duration-200 flex items-center gap-1 px-1 sm:px-2`}
      title={!selectedEvent ? "Bir olay seçin" : (isReading ? "Okuma devam ediyor..." : (isAutoPlaying ? 'Durdur' : 'Otomatik çalıştır'))}
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        {isAutoPlaying ? (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z M10 9v6m4-6v6" />
        ) : (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        )}
      </svg>
      <span className="text-sm hidden sm:inline">{isReading ? "Okunuyor..." : (isAutoPlaying ? 'Durdur' : 'Çalıştır')}</span>
    </button>
  );
};

export { AutoPlayButton as default, AutoPlayButton }; 